import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { RotateCcw, Save } from "lucide-react";

export const ProductCategoryPicker = ({ product, onSaved }) => {
  const [cats, setCats] = useState([]);
  const [picked, setPicked] = useState(product.manual_categories || product.categories || []);
  const [busy, setBusy] = useState("");
  const manual = !!product.manual_categories?.length;

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const name = (slug) => cats.find((c) => c.slug === slug)?.name || slug;

  const save = async (categories) => {
    setBusy("save");
    try {
      const r = await api.put(`/admin/products/${product.product_id}/categories`, { categories });
      toast.success(categories ? "Filing saved" : "Back on automatic filing");
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save filing");
    } finally { setBusy(""); }
  };

  return (
    <div className="sm:col-span-2 border-t border-brand-cream2 pt-4" data-testid="product-category-picker">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium">Categories</p>
        <Badge className={manual ? "bg-brand-green text-brand-cream" : "bg-brand-cream2 text-brand-ink/70"}
          data-testid="product-filing-state">
          {manual ? "Hand-picked" : "Automatic"}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-1" data-testid="product-auto-filing">
        Automatic filing: {(product.auto_categories || []).map(name).join(", ") || "—"}
      </p>
      <div className="flex flex-wrap gap-2 mt-3">
        {cats.map((c) => {
          const on = picked.includes(c.slug);
          return (
            <button key={c.slug} type="button" data-testid={`product-cat-${c.slug}`}
              onClick={() => setPicked(on ? picked.filter((x) => x !== c.slug) : [...picked, c.slug])}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                on ? "border-brand-green bg-brand-green/10" : "border-brand-cream2 hover:bg-brand-cream2/60"}`}>
              {c.name}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2 mt-3">
        <Button size="sm" className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2"
          disabled={!picked.length || busy === "save"} data-testid="product-cat-save"
          onClick={() => save(picked)}><Save className="w-3.5 h-3.5" /> Save filing</Button>
        <Button size="sm" variant="outline" className="rounded-full gap-2" disabled={!manual}
          data-testid="product-cat-revert" onClick={() => { setPicked(product.auto_categories || []); save(null); }}>
          <RotateCcw className="w-3.5 h-3.5" /> Revert to automatic
        </Button>
      </div>
    </div>
  );
};
